import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import { fallbackSectionAccess, usePanelAccessOptional } from '../context/PanelAccessContext';

type PanelSectionGuardProps = {
  /** Section panel (auth_role_default_access / auth_panel_access). */
  section: string;
  /** Si défini, le rôle courant doit être l’un de ceux-ci (contrainte métier en plus de la BDD). */
  rolesAnyOf?: number[];
  /** Repli pendant le chargement BDD ou hors boundary (défaut code historique). */
  defaultAccess: (roleId: number) => boolean;
  /** Fonction référent requise en plus (ex. Référent Admin), sauf pour referentBypassRoles. */
  requireReferentFunction?: string;
  /** Rôles qui accèdent sans la fonction référent (ex. administrateur = 2). */
  referentBypassRoles?: number[];
  children: React.ReactNode;
};

/**
 * Redirige vers /panel si le rôle ou l’accès effectif (individuel > rôle > défaut code) refuse la section.
 */
const PanelSectionGuard: React.FC<PanelSectionGuardProps> = ({
  section,
  rolesAnyOf,
  defaultAccess,
  requireReferentFunction,
  referentBypassRoles,
  children,
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const panelAccess = usePanelAccessOptional();
  const roleId = currentUser?.roles_id ?? 0;

  if (!currentUser) {
    return null;
  }

  const roleOk = !rolesAnyOf?.length || rolesAnyOf.includes(roleId);

  if (panelAccess?.loading) {
    return null;
  }

  let accessOk: boolean;
  if (panelAccess) {
    accessOk = panelAccess.hasAccessToSection(section);
  } else {
    accessOk = fallbackSectionAccess(section, roleId) || defaultAccess(roleId);
  }

  if (accessOk && requireReferentFunction) {
    const bypass = referentBypassRoles?.includes(roleId) ?? false;
    if (!bypass) {
      if (panelAccess && !panelAccess.loading) {
        accessOk = panelAccess.hasReferentFunction(requireReferentFunction);
      } else {
        accessOk = false;
      }
    }
  }

  if (!roleOk || !accessOk) {
    return <Navigate to="/panel/dashboard" replace />;
  }

  return <>{children}</>;
};

export default PanelSectionGuard;
